export const RESPONSIBLE_PERIODS = ['daily', 'weekly', 'monthly'];
export const SESSION_SINGLE_KEY = 'single';
export const MAX_SESSION_MINUTES = 1440;

export const EMPTY_RESPONSIBLE_LIMITS = {
  deposit: { current: {}, pending: {} },
  session: { current: {}, pending: {} },
  usage: { session: { periods: {}, singleStartedAt: null } },
};

export function formatDepositDisplay(value) {
  if (value == null || value === '') return '';
  const num = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(num)) return String(value);
  const fixed = Math.abs(num).toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${num < 0 ? '-' : ''}${grouped},${decPart}`;
}

export function formatDepositValue(value) {
  return formatDepositDisplay(value);
}

export function normalizeDepositInput(raw) {
  let value = String(raw || '').replace(/[^\d,]/g, '');
  const commaIndex = value.indexOf(',');
  if (commaIndex >= 0) {
    const intPart = value.slice(0, commaIndex);
    const decPart = value.slice(commaIndex + 1).replace(/,/g, '').slice(0, 2);
    value = `${intPart},${decPart}`;
  }
  if (!value) return '';
  const parts = value.split(',');
  const intDigits = parts[0].replace(/\D/g, '');
  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (parts.length > 1) return `${grouped},${parts[1]}`;
  if (value.endsWith(',')) return `${grouped},`;
  return grouped;
}

export function finalizeDepositInput(raw) {
  const parsed = parseDepositValue(raw);
  if (parsed == null) return '';
  return formatDepositDisplay(parsed);
}

export function parseDepositValue(raw) {
  const normalized = String(raw || '').trim().replace(/\s/g, '').replace(',', '.');
  if (!normalized) return null;
  const num = Number(normalized);
  return Number.isFinite(num) && num > 0 ? Math.round(num * 100) / 100 : null;
}

export function formatMinutesDisplay(value) {
  if (value == null || value === '') return '';
  const num = Math.round(Number(String(value).replace(/\s/g, '')));
  if (!Number.isFinite(num)) return String(value);
  return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function normalizeMinutesInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function parseMinutesValue(raw) {
  const normalized = String(raw || '').replace(/\s/g, '').trim();
  if (!normalized) return null;
  const num = Number(normalized);
  return Number.isFinite(num) && num > 0 ? Math.round(num) : null;
}

export function getSingleSessionStatus(limits) {
  const rule = limits?.session?.current?.[SESSION_SINGLE_KEY];
  const started = limits?.usage?.session?.singleStartedAt;
  if (!rule?.enabled || rule.value == null) return null;
  const limit = Math.round(Number(rule.value));
  if (!Number.isFinite(limit) || limit <= 0) return null;
  let elapsed = 0;
  if (started) {
    elapsed = Math.floor((Date.now() - new Date(started).getTime()) / 60000);
  }
  const remaining = Math.max(0, limit - elapsed);
  return {
    limit,
    elapsed,
    remaining,
    progress: limit > 0 ? remaining / limit : 0,
  };
}

export function parseSessionValue(raw) {
  return parseMinutesValue(raw);
}

export function splitSessionHours(value) {
  if (value == null || value === '') return { hours: '', minutes: '' };
  const totalMinutes = Math.round(Number(value) * 60);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return { hours: '', minutes: '' };
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours: String(hours), minutes: String(minutes) };
}

export function combineSessionHours(hoursRaw, minutesRaw) {
  const hours = Number(String(hoursRaw || '').trim() || 0);
  const minutes = Number(String(minutesRaw || '').trim() || 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours <= 0 && minutes <= 0) return null;
  const totalHours = hours + minutes / 60;
  return Math.round(totalHours * 100) / 100;
}

export function buildPeriodState(source = {}) {
  return RESPONSIBLE_PERIODS.reduce((acc, period) => {
    const item = source[period] || {};
    acc[period] = {
      enabled: Boolean(item.enabled),
      value: item.value != null ? String(item.value) : '',
    };
    return acc;
  }, {});
}

export function buildEditablePeriodState(limitGroup) {
  const pending = limitGroup?.pending || {};
  const current = limitGroup?.current || {};
  return RESPONSIBLE_PERIODS.reduce((acc, period) => {
    const item = pending[period] ?? current[period] ?? {};
    acc[period] = {
      enabled: Boolean(item.enabled),
      value: item.value != null ? String(item.value) : '',
    };
    return acc;
  }, {});
}

export function buildEditableSingleState(limitGroup) {
  const pending = limitGroup?.pending || {};
  const current = limitGroup?.current || {};
  const item = pending[SESSION_SINGLE_KEY] ?? current[SESSION_SINGLE_KEY] ?? {};
  return {
    enabled: Boolean(item.enabled),
    value: item.value != null ? formatMinutesDisplay(Math.round(Number(item.value))) : '',
  };
}

export function formatInputValue(type, value) {
  if (value == null || value === '') return '';
  if (type === 'deposit') return formatDepositValue(value);
  return String(value);
}

export function summarizeDepositLimit(current = {}, t, formatAmount) {
  for (const period of RESPONSIBLE_PERIODS) {
    const item = current[period];
    if (!item?.enabled || item.value == null) continue;
    return `${t(`account.responsible.${period}Limit`)}: ${formatAmount(item.value)}`;
  }
  return null;
}

export function summarizeSessionLimit(current = {}, t) {
  const single = current[SESSION_SINGLE_KEY];
  if (single?.enabled && single.value != null) {
    const minutes = formatMinutesDisplay(single.value);
    return `${t('account.responsible.sessionLimit')}: ${minutes} ${t('account.responsible.minutesShort')}`;
  }
  for (const period of RESPONSIBLE_PERIODS) {
    const item = current[period];
    if (!item?.enabled || item.value == null) continue;
    const { hours, minutes } = splitSessionHours(item.value);
    if (!hours && !minutes) continue;
    const parts = [];
    if (hours && hours !== '0') parts.push(`${hours} ${t('account.responsible.hoursShort')}`);
    if (minutes && minutes !== '0') parts.push(`${minutes} ${t('account.responsible.minutesShort')}`);
    return `${t(`account.responsible.${period}Limit`)}: ${parts.join(' ')}`;
  }
  return null;
}
